/**
 * PetalForm Component
 *
 * Form for creating and editing petals.
 * Supports all petal fields including flower, core type, title, content, and tags.
 */

import { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  PetalDto,
  CreatePetalDto,
  FlowerKey,
  CorePetalType,
  FLOWER_KEYS,
  CORE_PETAL_TYPES,
  FLOWER_LABELS,
  FLOWER_DESCRIPTIONS,
  CORE_PETAL_LABELS,
  useGetScenariosQuery,
} from '../../store/api/storyPrepApi';

interface PetalFormProps {
  petal?: PetalDto | null;
  onSubmit: (data: CreatePetalDto) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const INITIAL_FORM_STATE: CreatePetalDto = {
  flower: 'problem',
  coreType: 'story',
  title: '',
  content: '',
  domainKey: null,
  scenarioKey: null,
  tags: [],
};

export function PetalForm({
  petal,
  onSubmit,
  onCancel,
  isSubmitting,
}: PetalFormProps) {
  const [formData, setFormData] = useState<CreatePetalDto>(INITIAL_FORM_STATE);
  const [tagInput, setTagInput] = useState('');

  // Load scenarios for domain/scenario dropdown
  const { data: scenarios } = useGetScenariosQuery();

  // Populate form when editing
  useEffect(() => {
    if (petal) {
      setFormData({
        flower: petal.flower,
        coreType: petal.coreType,
        title: petal.title,
        content: petal.content,
        domainKey: petal.domainKey || null,
        scenarioKey: petal.scenarioKey || null,
        tags: petal.tags || [],
      });
    } else {
      setFormData(INITIAL_FORM_STATE);
    }
  }, [petal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) return;
    onSubmit(formData);
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tag],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter((t) => t !== tagToRemove),
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Get unique domains from scenarios
  const domains = scenarios
    ? [...new Set(scenarios.map((s) => s.domainKey))]
    : [];

  // Get scenarios for selected domain
  const domainScenarios = scenarios?.filter(
    (s) => s.domainKey === formData.domainKey
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, title: e.target.value }))
          }
          placeholder="e.g., The Time We Saved a Client 40%"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        />
      </div>

      {/* Flower and Core Type Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Flower */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Flower
          </label>
          <select
            value={formData.flower}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                flower: e.target.value as FlowerKey,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {FLOWER_KEYS.map((key) => (
              <option key={key} value={key}>
                {FLOWER_LABELS[key]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {FLOWER_DESCRIPTIONS[formData.flower]}
          </p>
        </div>

        {/* Core Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            value={formData.coreType}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                coreType: e.target.value as CorePetalType,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {CORE_PETAL_TYPES.map((type) => (
              <option key={type} value={type}>
                {CORE_PETAL_LABELS[type]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Content <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.content}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, content: e.target.value }))
          }
          placeholder="Tell your story, share your case study, or write your talking point..."
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
          required
        />
        <p className="mt-1 text-xs text-gray-500 text-right">
          {formData.content.length} characters
        </p>
      </div>

      {/* Domain and Scenario (optional) */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Domain (optional)
          </label>
          <select
            value={formData.domainKey || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                domainKey: e.target.value || null,
                scenarioKey: null, // Reset scenario when domain changes
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Domains</option>
            {domains.map((domain) => (
              <option key={domain} value={domain}>
                {domain.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Scenario (optional)
          </label>
          <select
            value={formData.scenarioKey || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                scenarioKey: e.target.value || null,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={!formData.domainKey}
          >
            <option value="">All Scenarios</option>
            {domainScenarios?.map((scenario) => (
              <option key={scenario.scenarioKey} value={scenario.scenarioKey}>
                {scenario.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tags (optional)
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a tag..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
          >
            Add
          </button>
        </div>
        {/* Tags display */}
        {(formData.tags?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.tags?.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Form actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
          className={clsx(
            'px-6 py-2 text-sm font-medium rounded-lg transition-all',
            isSubmitting || !formData.title.trim() || !formData.content.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          )}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </span>
          ) : petal ? (
            'Update Petal'
          ) : (
            'Create Petal'
          )}
        </button>
      </div>
    </form>
  );
}

export default PetalForm;
